import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Building2,
  Handshake,
  Target,
  FileCheck2,
  Users,
  TrendingUp,
  Mail,
  Landmark,
} from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/csr`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;
const csrEmail = "csr@sevaafoundation.com";

const seoKeywords = [
  "CSR NGO India",
  "SEVAA Foundation CSR",
  "Sevaa Foundation CSR",
  "Corporate Social Responsibility NGO",
  "CSR Partnership NGO",
  "CSR Collaboration India",
  "CSR Funding NGO",
  "CSR Donation NGO",
  "CSR Projects India",
  "CSR Implementation Partner",
  "CSR NGO Partner India",
  "CSR Activities for Companies",
  "Corporate Volunteering NGO",
  "Employee Engagement CSR",
  "CSR Education Project",
  "CSR Healthcare Project",
  "CSR Food Security Project",
  "CSR Women Empowerment Project",
  "CSR Tree Plantation Project",
  "CSR Elderly Care Project",
  "CSR Environmental Sustainability",
  "CSR Social Impact India",
  "CSR Reporting NGO",
  "Impact Assessment NGO",
  "Fund Utilization Certificate NGO",
  "80G Tax Exemption NGO",
  "Transparent NGO India",
  "CSR Eligible NGO",
  "NGO in Noida",
  "NGO in Jharkhand",
  "NGO in Chatra Jharkhand",
  "NGO in Pakur Jharkhand",
  "Corporate Charity Partnership",
  "Social Welfare NGO",
  "CSR पार्टनर NGO",
  "कॉर्पोरेट सोशल रिस्पॉन्सिबिलिटी",
  "CSR प्रोजेक्ट इंडिया",
  "CSR डोनेशन NGO",
  "कंपनी CSR पार्टनर",
  "समाज सेवा संस्था",
  "सेवा फाउंडेशन CSR",
  "शिक्षा CSR प्रोजेक्ट",
  "स्वास्थ्य CSR प्रोजेक्ट",
  "भोजन सहायता CSR",
  "महिला सशक्तिकरण CSR",
  "पेड़ लगाओ CSR",
  "बुजुर्ग सहायता CSR",
  "NGO ko donate kare",
  "दान करें",
];

const csrPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "CSR Opportunities | SEVAA Foundation",
  alternateName: "Corporate Social Responsibility Partnerships with SEVAA Foundation",
  url: pageUrl,
  inLanguage: ["en-IN", "hi-IN"],
  description:
    "Partner with SEVAA Foundation for CSR projects in education, healthcare, food security, women empowerment, tree plantation, elderly care, employee volunteering and measurable social impact.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: logoImage,
    email: csrEmail,
    areaServed: [
      {
        "@type": "Country",
        name: "India",
      },
      {
        "@type": "Place",
        name: "Noida",
      },
      {
        "@type": "Place",
        name: "Jharkhand",
      },
      {
        "@type": "Place",
        name: "Chatra",
      },
      {
        "@type": "Place",
        name: "Pakur",
      },
    ],
  },
  about: [
    "CSR Partnership",
    "Corporate Social Responsibility",
    "CSR Implementation",
    "Education CSR",
    "Healthcare CSR",
    "Food Security CSR",
    "Women Empowerment CSR",
    "Tree Plantation CSR",
    "Elderly Care CSR",
    "Employee Volunteering",
    "Impact Reporting",
    "Fund Utilization",
    "80G Tax Exemption",
  ],
  mainEntity: {
    "@type": "Service",
    name: "CSR Partnership and Social Impact Program",
    serviceType: "Corporate Social Responsibility Implementation",
    provider: {
      "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
      url: siteUrl,
      email: csrEmail,
    },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    description:
      "SEVAA Foundation partners with companies for CSR projects, employee volunteering, impact reporting and grassroots social development programs.",
  },
  potentialAction: {
    "@type": "ContactAction",
    name: "Initiate CSR Partnership",
    target: `mailto:${csrEmail}`,
  },
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
  alternateName: "Swadesh Sevaa Charitable Foundation",
  url: siteUrl,
  logo: logoImage,
  email: csrEmail,
  description:
    "SEVAA Foundation is a nonprofit organization working for education, healthcare, food security, women empowerment, elderly care, tree plantation, volunteering, CSR partnerships and social welfare.",
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  knowsAbout: [
    "CSR Partnerships",
    "Child Education",
    "Healthcare Support",
    "Food Security",
    "Women Empowerment",
    "Tree Plantation",
    "Old Age Support",
    "Employee Volunteering",
    "Impact Reporting",
    "Social Welfare",
  ],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: siteUrl,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "CSR Opportunities",
      item: pageUrl,
    },
  ],
};

const csrOfferings = [
  {
    title: "Impactful Projects",
    description:
      "Align your corporate CSR goals with verified on-ground projects across education, healthcare, food security, women empowerment, elderly care and environmental sustainability.",
    icon: <Target size={32} />,
    lineColor: "bg-emerald-500",
    keywords:
      "CSR education project, CSR healthcare project, CSR food security, CSR women empowerment, CSR tree plantation, CSR NGO partner",
  },
  {
    title: "Employee Engagement",
    description:
      "Organize customized volunteering drives and help employees connect directly with grassroots communities through meaningful social work.",
    icon: <Building2 size={32} />,
    lineColor: "bg-orange-500",
    keywords:
      "employee engagement CSR, corporate volunteering NGO, employee volunteer drive, CSR volunteering India",
  },
  {
    title: "Transparent Reporting",
    description:
      "Receive detailed impact assessment reports, fund utilization certificates, 80G documentation and transparent project updates.",
    icon: <Handshake size={32} />,
    lineColor: "bg-emerald-500",
    keywords:
      "CSR reporting NGO, impact assessment NGO, fund utilization certificate, 80G tax exemption NGO",
  },
];

const CSROpportunities = () => {
  return (
    <>
      <Helmet>
        <title>CSR Opportunities | Partner with SEVAA Foundation</title>

        <meta name="title" content="CSR Opportunities | Partner with SEVAA Foundation" />

        <meta name="description" content="Partner with SEVAA Foundation for CSR projects in education, healthcare, food security, women empowerment, tree plantation and transparent impact reporting." />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="CSR Opportunities | Partner with SEVAA Foundation"
        />

        <meta
          property="og:description"
          content="Build measurable corporate social impact through SEVAA Foundation CSR partnerships in education, healthcare, food security, women empowerment and sustainability."
        />

        <meta property="og:image" content={defaultImage} />
        <meta
          property="og:image:alt"
          content="SEVAA Foundation CSR Opportunities and Corporate Partnerships"
        />
                <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
<meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="CSR Opportunities | SEVAA Foundation NGO Partner"
        />

        <meta
          name="twitter:description"
          content="Partner with SEVAA Foundation for CSR projects, corporate volunteering and transparent impact reporting."
        />

        <meta name="twitter:image" content={defaultImage} />

                <meta name="twitter:image:alt" content="SEVAA Foundation CSR Opportunities and Corporate Partnerships" />
<script type="application/ld+json">
          {JSON.stringify(csrPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-[#0a1110] text-gray-100 font-poppins min-h-screen relative overflow-hidden">
        {/* Background Decor Texture */}
        <div
          className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none z-0"
          aria-hidden="true"
        ></div>

        {/* HERO SECTION */}
        <section
          aria-labelledby="csr-heading"
          className="relative h-[80dvh] lg:h-[70dvh] flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1716]/95 via-[#0f766e]/90 to-[#0f1716]/95 z-10"></div>

          <div className="relative z-20 max-w-6xl mx-auto px-6 lg:px-8 text-center flex flex-col items-center pt-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-8 shadow-xl"
            >
              <span className="w-2 h-2 bg-[#ffb000] rounded-full animate-pulse"></span>

              <span className="text-[#ffb000] text-[10px] md:text-xs font-black uppercase tracking-[0.2em]">
                Strategic CSR Partnerships
              </span>
            </motion.div>

            <motion.h1
              id="csr-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight mb-6 uppercase"
            >
              Empower Your <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb000] to-orange-400">
                Corporate Impact
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-teal-50 text-base md:text-xl font-medium max-w-3xl mx-auto mb-8 leading-relaxed opacity-90"
            >
              Partner with SEVAA Foundation to create sustainable, measurable
              social impact through Corporate Social Responsibility initiatives
              in education, healthcare, food security, women empowerment, tree
              plantation and elderly care.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto"
            >
              <a
                href={`mailto:${csrEmail}`}
                className="bg-[#ffb000] text-black font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl shadow-[0_0_40px_rgba(255,176,0,0.3)] hover:bg-white hover:shadow-[0_0_40px_rgba(255,255,255,0.4)] hover:-translate-y-1 transition-all duration-300 w-full sm:w-auto text-center"
                aria-label="Initiate CSR partnership with SEVAA Foundation"
              >
                Initiate Partnership
              </a>

              <a
                href="/legal-documents"
                className="bg-transparent text-white border-2 border-white/30 font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl hover:bg-white/10 hover:border-white transition-all duration-300 w-full sm:w-auto text-center"
                aria-label="View SEVAA Foundation legal documents and certificates"
              >
                View Documents
              </a>
            </motion.div>
          </div>
        </section>

        {/* TRUST STATS STRIP */}
        <section
          aria-labelledby="csr-trust-heading"
          className="relative z-20 -mt-12 px-6 lg:px-8"
        >
          <h2 id="csr-trust-heading" className="sr-only">
            SEVAA Foundation CSR Trust Indicators
          </h2>

          <div className="max-w-5xl mx-auto bg-[#0f1716] border border-white/10 rounded-3xl shadow-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-white/10">
            <article className="pt-4 md:pt-0">
              <div className="flex justify-center mb-3 text-[#ffb000]">
                <FileCheck2 size={34} />
              </div>

              <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
                80G
              </h3>

              <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#ffb000] font-bold">
                Tax Exemption
              </p>
            </article>

            <article className="pt-8 md:pt-0">
              <div className="flex justify-center mb-3 text-[#ffb000]">
                <TrendingUp size={34} />
              </div>

              <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
                100%
              </h3>

              <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#ffb000] font-bold">
                Transparent Reporting
              </p>
            </article>

            <article className="pt-8 md:pt-0">
              <div className="flex justify-center mb-3 text-[#ffb000]">
                <Landmark size={34} />
              </div>

              <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
                Pan-India
              </h3>

              <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#ffb000] font-bold">
                Execution Capability
              </p>
            </article>
          </div>
        </section>

        {/* CSR CORE PILLARS */}
        <section
          aria-labelledby="csr-value-heading"
          className="py-24 lg:py-32 relative z-10 px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16 max-w-2xl mx-auto"
            >
              <span className="text-emerald-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3 block">
                Value Proposition
              </span>

              <h2
                id="csr-value-heading"
                className="text-3xl md:text-5xl font-black text-white leading-tight mb-5"
              >
                Why Partner With{" "}
                <span className="text-[#ffb000]">SEVAA?</span>
              </h2>

              <p className="text-teal-50/70 text-base md:text-lg">
                SEVAA Foundation helps companies turn CSR goals into real,
                measurable and transparent grassroots impact.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {csrOfferings.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (index + 1) * 0.1 }}
                  className="bg-[#0f1716] border border-white/5 rounded-3xl p-10 relative group overflow-hidden shadow-xl hover:border-white/20 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-[#ffb000] mb-8 group-hover:scale-110 group-hover:bg-[#ffb000] group-hover:text-black transition-all duration-300">
                    {item.icon}
                  </div>

                  <h3 className="text-2xl font-black text-white mb-4">
                    {item.title}
                  </h3>

                  <p className="text-teal-50/70 leading-relaxed font-medium">
                    {item.description}
                  </p>

                  <div
                    className={`absolute bottom-0 left-8 right-8 h-1 ${item.lineColor} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}
                  ></div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CSR PROGRAM AREAS */}
        <section
          aria-labelledby="csr-programs-heading"
          className="pb-24 px-6 lg:px-8 relative z-10"
        >
          <div className="max-w-7xl mx-auto bg-[#0f1716] border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <span className="text-[#ffb000] text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3 block">
                CSR Focus Areas
              </span>

              <h2
                id="csr-programs-heading"
                className="text-3xl md:text-4xl font-black text-white mb-4"
              >
                Build Impact Across Key Social Causes
              </h2>

              <p className="text-teal-50/70 font-medium leading-relaxed">
                Companies can collaborate with SEVAA Foundation across multiple
                social impact areas depending on their CSR goals and community
                priorities.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Child Education CSR",
                "Healthcare CSR",
                "Food Security CSR",
                "Women Empowerment CSR",
                "Tree Plantation CSR",
                "Elderly Care CSR",
              ].map((area) => (
                <article
                  key={area}
                  className="rounded-2xl bg-white/5 border border-white/10 p-5 flex items-center gap-3 hover:border-[#ffb000]/50 transition-colors"
                >
                  <Users className="text-[#ffb000] shrink-0" size={22} />

                  <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                    {area}
                  </h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA BOX */}
        <section
          aria-labelledby="csr-cta-heading"
          className="pb-20 px-6 lg:px-8 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto bg-gradient-to-br from-[#0f766e] to-[#044d47] rounded-3xl p-10 md:p-16 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-10 border border-teal-600/30 overflow-hidden relative"
          >
            <div
              className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"
              aria-hidden="true"
            ></div>

            <div className="text-center md:text-left relative z-10 md:w-2/3">
              <h2
                id="csr-cta-heading"
                className="text-white text-3xl md:text-5xl font-black tracking-tight mb-4"
              >
                Ready to{" "}
                <span className="text-[#ffb000]">Collaborate?</span>
              </h2>

              <p className="text-teal-50 text-base md:text-xl font-medium mb-6">
                Let&apos;s design a CSR program that reflects your
                company&apos;s values and creates lasting change.
              </p>

              <a
                href={`mailto:${csrEmail}`}
                className="inline-flex items-center justify-center md:justify-start gap-3 text-white/80 font-semibold bg-black/20 w-fit px-5 py-3 rounded-xl border border-white/10 hover:text-white transition-colors"
                aria-label="Email SEVAA Foundation CSR team"
              >
                <Mail className="text-[#ffb000]" size={18} />
                {csrEmail}
              </a>
            </div>

            <div className="relative z-10 md:w-1/3 flex justify-center md:justify-end">
              <a
                href={`mailto:${csrEmail}`}
                className="bg-[#ffb000] text-black font-black text-sm uppercase tracking-widest px-8 py-5 rounded-2xl shadow-xl hover:bg-white hover:text-black transition-all hover:-translate-y-1 block text-center w-full sm:w-auto"
                aria-label="Email SEVAA Foundation partnership team"
              >
                Email Partnership Team
              </a>
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
};

export default CSROpportunities;     